import type {Page} from '@PosterWhiteboard/page/page.class';
import {PosterHistory} from '@PosterWhiteboard/poster/poster-history/poster-history';
import type {PosterEvents, PosterLoadObject, PosterObject} from '@PosterWhiteboard/poster/poster.types';
import {POSTER_GETTY_LIMIT, POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {SavePoster} from '@PosterWhiteboard/save-poster/save-poster.class';
import {User} from '@PosterWhiteboard/user/user.class';
import {PosterType} from '@PosterWhiteboard/poster-type/poster-type.class';
import {Observable} from '@PosterWhiteboard/observable';
import {PosterPages} from '@PosterWhiteboard/poster/poster-pages.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {AudioClips} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import {Clock} from '@PosterWhiteboard/clock/clock.class';
import {PosterDrawing} from '@PosterWhiteboard/poster/poster-drawing/poster-drawing';
import {Grid} from '@PosterWhiteboard/poster/grid.class';
import {Bleed} from '@PosterWhiteboard/poster/bleed.class';
import {Fold} from '@PosterWhiteboard/poster/fold.class';
import {PosterRedux} from '@PosterWhiteboard/poster/poster-redux.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {PosterScaling} from '@PosterWhiteboard/poster/poster-scaling.class';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {PosterModeDetails, PosterModeObject} from '@PosterWhiteboard/poster/poster-mode.class';
import {PosterMode, PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {openPosterInGenerationWarningModal} from '@Modals/poster-in-generation-warning-modal';
import {ImageBackground} from '@PosterWhiteboard/page/background/image-background.class';
import {v4 as uuidv4} from 'uuid';
import {ResizePoster} from '@PosterWhiteboard/poster/resize-poster.class';
import type {RGBA} from '@Utils/color.util';
import {DEFAULT_FILL_COLOR} from '@PosterWhiteboard/classes/fill.class';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {CornerPointsArray} from '@Utils/math.util';
import {isIOS, isSafari} from 'react-device-detect';
import {PosterVideoGeneration} from '@PosterWhiteboard/poster/poster-video-generation.class';
import {openMessageModal} from '@Modals/message-modal';
import {openDownloadModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openErrorModal} from '@Modals/error-modal';
import {ItemsMultiSelect} from '@PosterWhiteboard/poster/items-multi-select.class';
import {PosterPan} from '@PosterWhiteboard/poster/poster-pan.class';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {config, getFilterBackend, WebGLFilterBackend} from '@postermywall/fabricjs-2';
import {getPosterBackendObjectFromPoster} from '@PosterWhiteboard/poster/poster-frontend-to-backend';
import type {PosterObjectBackend} from '@PosterWhiteboard/poster/poster-backend.types';
import type {DeepPartial} from '@/global';
import type {FolderObject} from '../classes/folder.class';
import {Folder} from '../classes/folder.class';
import Emitter from '@/services/event-emitter';
import {EmbeddedEditorMessageType} from '@Libraries/embedded-editor';
import {initDefaultFabricControls} from '@PosterWhiteboard/poster/poster-item-controls';
import {resizePosterForResizeData} from '@PosterWhiteboard/libraries/resize-poster.library';
import type {PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import {notifyPuppeteer} from '@Libraries/puppeeter.library';
import {canPosterBePlayed, isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {getPosterObjectFromPosterBackendObject} from '@PosterWhiteboard/poster/poster-backend';
import {updateCurrentUserId, updateIsTimelineModeActive} from '@Components/poster-editor/poster-editor-reducer';
import {updateIsAutosaveEnabled} from '@/reducers/user-preferences.reducer';
import '@PosterWhiteboard/items/item/item';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import {PosterPreview} from '@PosterWhiteboard/save-poster/poster-preview.class';
import {isPosterVideo} from '@Libraries/poster-object.library';

const WEBGL_TEXTURE_SIZE = 4096;
const GENERATION_WEBGL_TEXTURE_SIZE = 10800;
const UPDATE_FROM_OBJECT_KEY = 'poster-update-from-object';
const TEMPLATE_PROCESSING_KEY = 'poster-template-processing';
export const PAYG_LIMIT_FOR_NUM_PAGES = 3;
export const PREMIUM_LIMIT_FOR_NUM_PAGES = 99;

export interface PosterOpts {
  mode: PosterModeObject;
  savedPosterPreviewUrls: string[];
  folder?: FolderObject;
  isHighRes?: boolean;
  ageOfGeneratedOrder?: number;
}

interface ReplacePosterOpts {
  maintainDimensions?: boolean;
  isForGalleryTemplate?: boolean;
  undoable?: boolean;
  showResizeSuccessMessage?: boolean;
}

export class Poster extends Observable<PosterEvents> {
  public id = '';
  public hashedID = '';
  public name = '';
  public description = '';
  public folder: Folder;
  public isTemplate = false;
  public isPublic = false;
  public isCopyable = true;
  public isPurchaseable = true;
  public createdOn = 0;
  public lastModified = 0;
  public idLastModifier = '';
  public isInternal = false;
  public idTemplate = '';
  public idGalleryTemplate?: string;
  public idGalleryTemplateCreator = 0;
  public seoName = '';
  public version: number = POSTER_VERSION.CURRENT;
  public height!: number;
  public width!: number;
  public type!: PosterType;
  public units = '';
  public creator?: User;
  public owner?: User;
  public userWidth = 0;
  public userHeight = 0;
  public uploadingImages: string[] = [];
  public audioClips: AudioClips;
  public clock;
  public pages: PosterPages;

  public HTMLElement: HTMLDivElement;
  private viewportPageId = '';
  public selectedPageId: string | undefined;
  public isHighRes = false;
  public ageOfGeneratedOrder?: number;
  public mode: PosterMode;
  public drawing: PosterDrawing;
  public pan: PosterPan;
  public itemsMultiSelect: ItemsMultiSelect;
  public grid: Grid;
  public bleed: Bleed;
  public fold: Fold;
  public scaling: PosterScaling;
  public copyKey: string;
  public isInitialized = false;
  public lastAddedTextColor: RGBA = DEFAULT_FILL_COLOR;
  public resizePoster: ResizePoster;
  public videoGeneration: PosterVideoGeneration;
  public readonly history: PosterHistory;
  public readonly savePoster: SavePoster;
  public readonly redux: PosterRedux;
  public readonly posterPreview: PosterPreview;

  private pauseTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private singlePagePlaybackPageId?: string;
  public isTemplateApplied: boolean;
  public inProgressChanges: Record<string, boolean>;

  public selectMovedItems(movedItemUIDs: string[]): void {
    const pageIdWithMovedItemUIDsMap = new Map<string, string[]>();

    for (const itemUid of movedItemUIDs) {
      const item = this.getItemForId(itemUid);
      if (!item) {
        continue;
      }

      const pageId = item.page.hashedID;
      if (!pageIdWithMovedItemUIDsMap.has(pageId)) {
        pageIdWithMovedItemUIDsMap.set(pageId, []);
      }
      pageIdWithMovedItemUIDsMap.get(pageId)?.push(itemUid);
    }

    const earliestPageId = this.pages.pageOrder.find((pid) => pageIdWithMovedItemUIDsMap.has(pid));
    if (!earliestPageId) {
      return;
    }

    const uids = pageIdWithMovedItemUIDsMap.get(earliestPageId) ?? [];
    const earliestPage = this.pages.getPage(earliestPageId);
    if (!earliestPage || uids.length === 0) {
      return;
    }

    const fabricObjects: FabricObject[] = [];

    for (const uid of uids) {
      const item = this.getItemForId(uid);
      if (item?.fabricObject) {
        fabricObjects.push(item.fabricObject);
      }
    }

    earliestPage.activeSelection.selectFabricObjects(fabricObjects);
  }

  public constructor(htmlElement: HTMLDivElement, modeDetails: PosterModeDetails) {
    super();
    const posterDiv = document.createElement('div');
    posterDiv.className = 'pmw-page-container-parent';
    htmlElement.appendChild(posterDiv);
    this.HTMLElement = posterDiv;

    this.drawing = new PosterDrawing(this);
    this.pan = new PosterPan(this);
    this.history = new PosterHistory(this);
    this.savePoster = new SavePoster(this);
    this.posterPreview = new PosterPreview(this);
    this.redux = new PosterRedux(this);
    this.folder = new Folder(this);
    this.pages = new PosterPages(this);
    this.grid = new Grid(this);
    this.bleed = new Bleed(this);
    this.fold = new Fold(this);
    this.type = new PosterType();
    this.creator = new User();
    this.audioClips = new AudioClips(this);
    this.videoGeneration = new PosterVideoGeneration(this);
    this.scaling = new PosterScaling(this);
    this.resizePoster = new ResizePoster(this);
    this.mode = new PosterMode(modeDetails);
    this.itemsMultiSelect = new ItemsMultiSelect(this);
    this.copyKey = uuidv4();
    this.isTemplateApplied = false;
    this.inProgressChanges = {};
    Emitter.on('user:loggedIn', this.onUserLoggedIn.bind(this));
    this.clock = new Clock(this, {
      onEnd: this.onPosterEnd.bind(this),
      onTimeUpdated: this.onTimeUpdated.bind(this),
    });

    this.initFabricDefaultValues();
    initDefaultFabricControls();
  }

  public async updateAutoSaveOnUserLogin(): Promise<void> {
    try {
      const isAutoSaveEnabled = (await window.PMW.writeLocal('user/isAutosaveEnabled')) as boolean;
      window.PMW.redux.store.dispatch(updateIsAutosaveEnabled(isAutoSaveEnabled));
      if (isAutoSaveEnabled) {
        this.savePoster.initAutoSave();
      }
    } catch (e) {
      console.error(e);
    }
  }

  private onUserLoggedIn(): void {
    const userId = window.PMW.getUserId();
    if (typeof userId === 'number') {
      window.PMW.redux.store.dispatch(updateCurrentUserId(userId));
      void this.updateAutoSaveOnUserLogin();
    }
  }

  /**
   * This represents the total width canvas if we didn't clip it when goes out of visible area
   */
  public getCanvasTotalWidth(): number {
    return this.scaling.scale * this.width;
  }

  /**
   * This represents the total height canvas if we didn't clip it when goes out of visible area
   */
  public getCanvasTotalHeight(): number {
    return this.scaling.scale * this.height;
  }

  public isPosterInGeneration(): boolean {
    if (!window.PMW.currentUserOnPageLoad?.isSuper) {
      return this.ageOfGeneratedOrder === 0;
    }
    return false;
  }

  public isPosterTemplate(): boolean {
    return this.isPublic && !this.isInternal;
  }

  /**
   * In builder the scrollbar don't actually scroll the canvas but transform the content within. Thus, not slowing the experience on zoom
   */
  public hasFakeScrollbars(): boolean {
    return isEditorMobileVariant() && !this.mode.isGeneration();
  }

  public onPosterInit(): void {
    const currentPageOrder = this.pages.getPageOrder(this.getCurrentPageId());
    if (currentPageOrder !== undefined) {
      void this.pages.seekToPageIndex(currentPageOrder);
    }
    // this.updatePagesVisibility();
    this.history.reset();
    this.isInitialized = true;
    this.savePoster.initAutoSave();
    this.redux.updateReduxData();
    this.showPosterInGenerationWarning();
    this.pan.initCanvasPanEvent();
  }

  private showPosterInGenerationWarning(): void {
    if (this.mode.details.type !== PosterModeType.REGEN) {
      if (this.id && this.creator !== null) {
        if (this.isPosterInGeneration()) {
          openPosterInGenerationWarningModal();
        }
      }
    }
  }

  public getEditLink(): string {
    return window.PMW.util.site_url(`posterbuilder/load/${this.hashedID}`);
  }

  public getWebpageLink(): string {
    return window.PMW.util.site_url(`posterbuilder/view/${this.hashedID}`);
  }

  public getDuration(): number {
    return this.pages.getTotalDuration();
  }

  public dispose(): void {
    this.savePoster.onDispose();
    this.posterPreview.onDispose();
    this.redux.onDispose();
    this.pages.dispose();
    this.scaling.dispose();
    this.HTMLElement.innerHTML = '';
  }

  public setCurrentPage(pageId: string): void {
    if (pageId !== this.viewportPageId && this.pages.getPage(pageId)) {
      this.viewportPageId = pageId;
      this.redux.updateReduxData();
      if (this.isTimelineMode()) {
        this.updateCanvasVisibility();
      }
    }
  }

  public updatePagesVisibility(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      if (page.hashedID === this.getCurrentPageId()) {
        this.pages.showPage(page.hashedID);
      } else {
        this.pages.hidePage(page.hashedID);
      }
    }
  }

  public showPreviousPage(): void {
    const previousPageIndex = this.pages.getPreviousPageIndex();
    if (previousPageIndex !== undefined) {
      void this.pages.seekToPageIndex(previousPageIndex);
    }
  }

  public showNextPage(): void {
    const nextPageIndex = this.pages.getNextPageIndex();
    if (nextPageIndex !== undefined) {
      void this.pages.seekToPageIndex(nextPageIndex);
    }
  }

  public setSelectedPageAndUpdateView(pageId: string): void {
    this.setCurrentPage(pageId);
    // this.updatePagesVisibility();
  }

  public clearItemSelection(): void {
    this.pages.clearSelection();
  }

  public clearSelection(): void {
    this.clearItemSelection();
    this.audioClips.unselectAudioPlaylist();
  }

  public deleteItemById(uid: string, undoable = true): void {
    this.pages.getPageWithItemId(uid)?.items.removeItem(uid, undoable);
  }

  public deleteItemByIds(uids: string[], undoable = true): void {
    showLoading('Deleting');
    for (let index = 0; index < uids.length; index += 1) {
      this.deleteItemById(uids[index], undoable ? index === uids.length - 1 : false);
    }
    hideLoading('Deleting');
  }

  /**
   * Exports the freebie design as a s3 URL for the embedded builder. The URL is then returned to the
   * user and passed to the callback of the openEditor or openWizard function
   */
  public async exportDesign(): Promise<void> {
    if (this.isVideo()) {
      openMessageModal({
        title: window.i18next.t('pmwjs_video_export_title'),
        text: window.i18next.t('pmwjs_video_export_desc'),
        ctaButton: {
          text: window.i18next.t('pm_download_my_project'),
          onClick: openDownloadModal,
        },
      });
    } else {
      showLoading('ExportFreeDownload', {
        text: window.i18next.t('pmwjs_exporting'),
      });

      try {
        const isWebdownload = await this.isWebDownload(
          this.id,
          this.isPosterPremium(),
          this.type.name,
          this.idGalleryTemplate ?? '',
          this.idTemplate,
          this.savePoster.hasUnsavedChanges(),
          this.isVideo()
        );

        const bits = this.getCurrentPage().pageSnapshot.getFreeDownloadSnapshot(isWebdownload);
        const response = (await window.PMW.writeLocal('posterbuilder/saveExportedPreview', {
          design: bits,
        })) as string;
        window.parent.postMessage(`${EmbeddedEditorMessageType.FREEBIE_URL}~${encodeURI(response)}`, '*');
      } catch (e) {
        console.error(e);
        openErrorModal({
          message: window.i18next.t('pmwjs_checkout_download_error'),
        });
      } finally {
        hideLoading('ExportFreeDownload');
      }
    }
  }

  /**
   * Gets a boolean value in the data variable denoting whether the design is a web download or not
   */
  public isWebDownload(
    idPoster: string,
    isPremium: boolean,
    posterType: string,
    idGalleryTemplate: string,
    idTemplate: string,
    unsavedChanges: boolean,
    isVideo: boolean
  ): Promise<boolean> {
    return window.PMW.readLocal(`posterbuilder/isWebDownload/${idPoster ?? 0}/${isPremium ? '1' : '0'}?${new Date().getTime()}`, {
      pt: posterType,
      idgt: idGalleryTemplate,
      idt: idTemplate,
      uc: unsavedChanges ? '1' : '0',
      iv: isVideo ? '1' : '0',
    }) as Promise<boolean>;
  }

  public deleteSelection(): void {
    if (this.audioClips.hasActiveSelection()) {
      this.audioClips.deleteSelectedAudioItem();
      return;
    }

    this.getCurrentPage().activeSelection.deleteSelectedItems();
  }

  public clearSelectedPageId(): void {
    this.updateSelectedPageId(undefined);
  }

  public updateSelectedPageId(pageHashedId: string | undefined): void {
    if (!pageHashedId && this.selectedPageId) {
      this.selectedPageId = undefined;
      return;
    }

    this.selectedPageId = pageHashedId;

    this.redux.updateReduxData();
  }

  public getCurrentPageId(): string {
    if (this.selectedPageId) {
      return this.selectedPageId;
    }

    return this.viewportPageId;
  }

  public getCurrentPage(): Page {
    if (this.selectedPageId) {
      const pageWithSelection = this.pages.getPage(this.selectedPageId);
      if (!pageWithSelection) {
        throw new Error(`There is no page for hashedId: ${this.selectedPageId}`);
      }

      return pageWithSelection;
    }

    const currentPage = this.pages.getPage(this.viewportPageId);
    if (!currentPage) {
      throw new Error('No current page');
    }

    return currentPage;
  }

  public getBackendObject(): PosterObjectBackend {
    return getPosterBackendObjectFromPoster(this.toObjectForLoad());
  }

  public toObjectForLoad(): PosterLoadObject {
    return {
      hashedID: this.hashedID,
      type: this.type.toObject(),
      audioClips: this.audioClips.toObject(),
      userWidth: this.userWidth,
      userHeight: this.userHeight,
      units: this.units,
      version: this.version,
      name: this.name,
      width: this.width,
      height: this.height,
      id: this.id,
      idTemplate: this.idTemplate,
      description: this.description,
      isTemplate: this.isTemplate,
      isPublic: this.isPublic,
      isCopyable: this.isCopyable,
      isPurchaseable: this.isPurchaseable,
      createdOn: this.createdOn,
      lastModified: this.lastModified,
      idLastModifier: this.idLastModifier,
      isInternal: this.isInternal,
      idGalleryTemplate: this.idGalleryTemplate,
      idGalleryTemplateCreator: this.idGalleryTemplateCreator,
      seoName: this.seoName,
      creator: this.creator?.toObject(),
      pages: this.pages.toObject(),
    };
  }

  public toObject(): PosterObject {
    return {
      ...this.toObjectForLoad(),
      scaling: this.scaling.toObject(),
      bleed: this.bleed.toObject(),
      folder: this.folder.toObject(),
      duration: this.getDuration(),
      fold: this.fold.toObject(),
      grid: this.grid.toObject(),
      mode: this.mode.toObject(),
      drawing: this.drawing.toObject(),
      copyKey: this.copyKey,
      isInitialized: this.isInitialized,
      itemsMultiSelect: this.itemsMultiSelect.toObject(),
      uploadingImages: [...this.uploadingImages],
    };
  }

  public override copyVals(posterObject: DeepPartial<PosterObject> = {}): void {
    const {creator, type, ...obj} = posterObject;
    super.copyVals(obj);
    this.type.copyVals(type);
    this.creator?.copyVals(creator);
  }

  private addInProgressChanges(key: string): void {
    this.inProgressChanges[key] = true;
  }

  private removeInProgressChanges(key: string): void {
    delete this.inProgressChanges[key];
  }

  public async updateFromObject(
    posterObject: DeepPartial<PosterObject>,
    {updateRedux = true, undoable = true, fireModifiedEvent = true, refreshActiveSelection = false, checkForDurationUpdate = false, onItemAddFail}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    this.addInProgressChanges(UPDATE_FROM_OBJECT_KEY);
    const {pages, mode: _mode, scaling, audioClips, folder, grid, fold, bleed, drawing, itemsMultiSelect, ...pageObjectWithoutPages} = posterObject;
    const didDimensionsUpdate = this.areDimensionsDifferentFromObject(posterObject);
    let renderAllCanvases = false;

    this.copyVals({
      ...pageObjectWithoutPages,
    });

    try {
      if (scaling) {
        this.scaling.updateFromObject(scaling, false);
      }

      if (pages) {
        await this.pages.updateFromObject(pages, {
          updateRedux: false,
          undoable: false,
          fireModifiedEvent: false,
          refreshActiveSelection,
          checkForDurationUpdate,
          onItemAddFail,
        });
      }
      if (audioClips) {
        await this.audioClips.updateFromObject(audioClips, {updateRedux: false, fireModifiedEvent: false, undoable: false});
      }

      if (folder) {
        this.folder.updateFromObject(folder);
      }
      if (grid) {
        this.grid.updateFromObject(grid);
        renderAllCanvases = true;
      }
      if (bleed) {
        this.bleed.updateFromObject(bleed);
        renderAllCanvases = true;
      }
      if (fold) {
        this.fold.updateFromObject(fold);
        renderAllCanvases = true;
      }
      if (drawing) {
        this.drawing.updateFromObject(drawing);
      }
      if (itemsMultiSelect) {
        this.itemsMultiSelect.updateFromObject(itemsMultiSelect);
      }
    } finally {
      this.removeInProgressChanges(UPDATE_FROM_OBJECT_KEY);
    }

    this.validateValues(posterObject, undoable);

    if (undoable) {
      this.history.addPosterHistory();
    }

    if (didDimensionsUpdate) {
      this.pages.applyScales();
      if (!this.mode.isGeneration()) {
        this.scaling.fitToScreen();
      }
    }

    if (renderAllCanvases) {
      this.renderAllCanvases();
    }

    if (fireModifiedEvent) {
      this.fire('modified');
    }
    if (updateRedux) {
      this.redux.updateReduxData();
    }
  }

  public renderAllCanvases(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      page.fabricCanvas.requestRenderAll();
    }
  }

  public refreshAllVisibleCanvases(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      page.refreshVisibleCanvas();
    }
  }

  public isScrollMode(): boolean {
    return !this.isTimelineMode();
  }

  public isTimelineMode(): boolean {
    return window.PMW.redux.store.getState().posterEditor.isTimelineModeActive ?? false;
  }

  public updateIsTimelineMode(val: boolean): void {
    window.PMW.redux.store.dispatch(updateIsTimelineModeActive(val));
    this.updateCanvasVisibility();
  }

  private updateCanvasVisibility(): void {
    const isTimelineMode = this.isTimelineMode();
    const containers = document.querySelectorAll<HTMLElement>('.pmw-page-container');

    if (isTimelineMode) {
      const currentPageId = this.getCurrentPage().hashedID;
      containers.forEach((container) => {
        const dataId = container.getAttribute('data-id');
        if (dataId !== currentPageId) {
          container.classList.add('_hidden');
        } else {
          container.classList.remove('_hidden');
          this.pages.getPage(currentPageId)?.refreshVisibleCanvas();
        }
      });
    } else {
      containers.forEach((container) => {
        container.classList.remove('_hidden');
      });
    }
  }

  public isGalleryTemplate(): boolean {
    return this.id === this.idGalleryTemplate;
  }

  public isSuperUserEditingOthersPoster(): boolean {
    if (window.PMW.currentUserOnPageLoad === undefined) {
      return false;
    }

    return window.PMW.currentUserOnPageLoad.isSuper && window.PMW.currentUserOnPageLoad.id !== this.creator?.id;
  }

  private validateValues(posterObject: DeepPartial<PosterObject>, undoable: boolean): void {
    if (undoable) {
      if (posterObject.name === '') {
        void this.updateFromObject(
          {
            name: 'Untitled',
          },
          {
            undoable: false,
          }
        );
      }
    }
  }

  public isVideo(): boolean {
    return isPosterVideo(this.toObjectForLoad());
  }

  public getSelectedItems(): ItemType[] {
    return this.getCurrentPage().getSelectedItems();
  }

  public hasActiveSelection(): boolean {
    return this.getSelectedItems().length !== 0;
  }

  public getSelectedViews(): FabricObject[] {
    return this.getCurrentPage().activeSelection.getActiveObjects();
  }

  public getItemForId(uid: string): ItemType | undefined {
    return this.pages.getItemForId(uid);
  }

  public getPageForItem(uid: string): Page | undefined {
    for (const page of Object.values(this.pages.pagesHashMap)) {
      if (page.items.getItem(uid)) {
        return page;
      }
    }
    return undefined;
  }

  public getTranscriptItemFromSubtitleId = (idSubtitle: string): TranscriptItem | undefined => {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      for (const [, item] of Object.entries(page.items.itemsHashMap)) {
        if (item.isTranscript()) {
          if (idSubtitle in item.subtitlesHashmap) {
            return item;
          }
        }
      }
    }
    return undefined;
  };

  private onPosterEnd(): void {
    if (this.mode.isWebpage()) {
      void this.loopPoster();
    } else {
      void this.pause();
      void this.audioClips.seekToPosterTime(0);
    }
  }

  private async loopPoster(): Promise<void> {
    await this.seek(0);
    await this.play();
  }

  private onTimeUpdated(time: number): void {
    this.fire('time:updated', time);
    this.checkSinglePagePlaybackEnd(time);
  }

  private checkSinglePagePlaybackEnd(currentTime: number): void {
    if (this.singlePagePlaybackPageId === undefined || !this.isPlaying()) {
      return;
    }

    try {
      const pageEndTime = this.pages.getEndTimeForPage(this.singlePagePlaybackPageId);

      if (currentTime >= pageEndTime) {
        this.singlePagePlaybackPageId = undefined;
        void this.pause();
      }
    } catch {
      this.singlePagePlaybackPageId = undefined;
      void this.pause();
    }
  }

  public getGettyImageCount(): number {
    let count = 0;

    for (const page of this.pages.getPagesInOrder()) {
      if (
        page.background.details instanceof ImageBackground &&
        page.background.details.imageBackgroundItem.isNonPurchasedGettyImage() &&
        !page.background.details.imageBackgroundItem.isRemoved
      ) {
        count += 1;
      }

      count += page.items.getGettyImageCount();
    }

    return count;
  }

  public getExtractedGettyStickerCount(): number {
    let count = 0;

    for (const page of this.pages.getPagesInOrder()) {
      count += page.items.getExtractedGettyStickerCount();
    }

    return count;
  }

  public updateTitle(): void {
    document.title = `${this.name} | PosterMyWall`;
  }

  public getGettyVideoCount(): number {
    let count = 0;

    for (const page of this.pages.getPagesInOrder()) {
      count += page.items.getGettyVideoCount();
    }

    return count;
  }

  public isPlaying(): boolean {
    return this.clock.isPlaying();
  }

  public getCurrentTime(): number {
    return this.clock.getCurrentTime();
  }

  public async stop(): Promise<void> {
    this.singlePagePlaybackPageId = undefined;
    this.clock.stop();
    this.audioClips.pause();
    await this.pages.stop();
    this.redux.updateIsPlayingInRedux();
  }

  public async pause(): Promise<void> {
    this.invalidatePauseTimeout();
    this.singlePagePlaybackPageId = undefined;

    this.clock.pause();
    this.audioClips.pause();
    await this.pages.pause();
    this.redux.updateIsPlayingInRedux();
  }

  public async seek(time: number): Promise<void> {
    this.invalidatePauseTimeout();

    this.clock.setCurrentTime(time);
    await this.audioClips.seekToPosterTime(time);
    const currentPage = this.getCurrentPage();
    currentPage.onSeekPage();
    this.pages.updateSelectedPageForTime(time);
    if (!this.isPlaying()) {
      currentPage.fabricCanvas.requestRenderAll();
    }
    this.fire('seeked');
  }

  public async seekPosterToSelectedAudioItem(): Promise<void> {
    const selectedAudioItem = this.audioClips.getSelectedAudioItem();
    if (selectedAudioItem) {
      await window.posterEditor?.whiteboard?.seek(this.audioClips.getTimeForAudioItem(selectedAudioItem.uid));
    }
  }

  public async pauseOrPlayBetweenStartAndEndTime(start: number, end: number): Promise<void> {
    if (this.isPlaying()) {
      await this.pause();
      return;
    }

    await this.seek(start);
    this.singlePagePlaybackPageId = undefined;

    try {
      this.clock.play();
      this.audioClips.play();
      await this.getCurrentPage().playPage();
      this.redux.updateIsPlayingInRedux();

      const duration = (end - start) * 1000;
      this.pauseTimeoutId = setTimeout(() => {
        void (async (): Promise<void> => {
          await this.pause();
          this.pauseTimeoutId = null;
        })();
      }, duration);
    } catch (e) {
      console.error(e);
      await this.pause();
    }
  }

  public invalidatePauseTimeout(): void {
    if (this.pauseTimeoutId !== null) {
      clearTimeout(this.pauseTimeoutId);
      this.pauseTimeoutId = null;
    }
  }

  public async play(pageId?: string): Promise<void> {
    if (!canPosterBePlayed()) {
      return;
    }

    this.invalidatePauseTimeout();
    this.singlePagePlaybackPageId = undefined;

    try {
      const playOnlySinglePage = pageId !== undefined || this.isScrollMode();
      const pageToPlayId = pageId ?? this.getCurrentPageId();
      const page = this.pages.getPage(pageToPlayId);
      if (!page) {
        return;
      }

      if (!page.isTimeWithinPage()) {
        await this.pages.seekToPage(page.hashedID);
      }

      if (playOnlySinglePage) {
        this.singlePagePlaybackPageId = page.hashedID;
      }

      this.setCurrentPage(page.hashedID);
      this.clock.play();
      this.audioClips.play();
      await page.playPage();
      this.redux.updateIsPlayingInRedux();
    } catch (e) {
      console.error(e);
      void this.pause();
    }
  }

  public async replayPoster(): Promise<void> {
    await this.stop();
    await this.play();
  }

  public async replayPage(pageId: string): Promise<void> {
    const pageOrder = this.pages.pageOrder.indexOf(pageId);
    await this.pause();
    await this.pages.seekToPageIndex(pageOrder);
    await this.play();
  }

  public hasMaxNumberofGettyVideoItems(): boolean {
    return this.getGettyVideoCount() >= POSTER_GETTY_LIMIT.VIDEOS;
  }

  public hasMaxNumberOfGettyImages(): boolean {
    return this.getGettyImageCount() >= POSTER_GETTY_LIMIT.IMAGES;
  }

  public hasMaxNumberOfExtractedGettyStickers(): boolean {
    return this.getExtractedGettyStickerCount() >= POSTER_GETTY_LIMIT.EXTRACTED_GETTY_STICKER;
  }

  public getFontsOnPoster(withVariation = false): string[] {
    const pages = Object.values(this.pages.pagesHashMap);
    const fonts: string[] = [];

    for (const eachPage of pages) {
      fonts.push(...eachPage.getFontsOnPage(withVariation));
    }

    return fonts;
  }

  private areDimensionsDifferentFromObject(posterObject: DeepPartial<PosterObject>): boolean {
    return (posterObject.width !== undefined && posterObject.width !== this.width) || (posterObject.height !== undefined && posterObject.height !== this.height);
  }

  public togglePlayPause(): void {
    if (this.isPlaying()) {
      void this.pause();
    } else {
      void this.play();
    }
  }

  public getCornerPointsArray(): CornerPointsArray {
    return [
      {
        x: 0,
        y: 0,
      },
      {
        x: this.width,
        y: 0,
      },
      {
        x: this.width,
        y: this.height,
      },
      {
        x: 0,
        y: this.height,
      },
    ];
  }

  public deleteItemsNotVisibleOnPoster(undoable = true): void {
    const itemsToDelete = [];
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      for (const [, item] of Object.entries(page.items.itemsHashMap)) {
        if (!item.isItemVisibleOnPoster() || (item.isSlideshow() && !item.isValid())) {
          itemsToDelete.push(item.uid);
        }
      }
    }

    this.deleteItemByIds(itemsToDelete, undoable);
  }

  public setMenuItemsWrappingInfo(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      for (const [, item] of Object.entries(page.items.itemsHashMap)) {
        if (item.isMenu()) {
          item.wrappingInfo = item.getWrappedLines();
        }
      }
    }
  }

  public isPosterPremium(): boolean {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      if (page.isPremium()) {
        return true;
      }
    }
    return false;
  }

  public validatePagesIntroAnimation(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      page.items.checkForAnimationOnNoItems();
    }
  }

  public hasClickableItem(): boolean {
    for (const page of this.pages.getPagesInOrder()) {
      if (page.items.hasClickableItem()) {
        return true;
      }
    }
    return false;
  }

  public getAllItems(): ItemType[] {
    let items: ItemType[] = [];
    for (const page of this.pages.getPagesInOrder()) {
      items = items.concat(page.items.getItems());
    }
    return items;
  }

  public getAllImageItems(): ImageItem[] {
    let items: ImageItem[] = [];
    for (const page of this.pages.getPagesInOrder()) {
      items = items.concat(page.items.getImageItems());
    }
    return items;
  }

  public addToUploadingImages(id: string, updateRedux = false): void {
    if (!this.uploadingImages.includes(id)) {
      this.uploadingImages.push(id);
      if (updateRedux) {
        this.redux.updateReduxData();
      }
    }
  }

  public removeFromUploadingImages(id: string, updateRedux = false): void {
    const index = this.uploadingImages.indexOf(id);
    if (index !== -1) {
      this.uploadingImages.splice(index, 1);
      if (updateRedux) {
        this.redux.updateReduxData();
      }
    }
  }

  public areAnyImagesUploading(): boolean {
    return this.uploadingImages.length > 0;
  }

  public areAnyItemsMoving(): boolean {
    return this.redux.areAnyPosterItemsMoving();
  }

  public getImageSrcOfUploadingImage(): string | undefined {
    const imageSrcItem = this.getCurrentPage().items.getItem(this.uploadingImages[0]);
    if (imageSrcItem && (imageSrcItem.isImage() || imageSrcItem.isImageSlide() || imageSrcItem.isImageBackground())) {
      return imageSrcItem.uploadingImageData?.dataUrl;
    }
    return undefined;
  }

  private initFabricDefaultValues(): void {
    this.initFabricWebgl();
    if (this.mode.isGeneration()) {
      config.perfLimitSizeTotal = 4194304;
    }
  }

  private initFabricWebgl(): void {
    config.enableGLFiltering = !(isSafari || isIOS) && this.mode.canUseWebGL();
    config.textureSize = this.mode.isGeneration() ? GENERATION_WEBGL_TEXTURE_SIZE : WEBGL_TEXTURE_SIZE;
    const filter = getFilterBackend();
    if (filter instanceof WebGLFilterBackend) {
      filter.canvas.addEventListener('webglcontextlost', () => {
        console.warn('WebGL context lost');
        notifyPuppeteer('webgl-context-lost');
      });
    }
  }

  public startTemplateLoading(): void {
    this.addInProgressChanges(TEMPLATE_PROCESSING_KEY);
  }

  public async updateCurrentTimeIfNeeded(): Promise<void> {
    if (!this.isVideo()) {
      await this.seek(0);
      return;
    }

    if (this.getCurrentTime() > this.getDuration()) {
      await this.seek(this.getDuration());
    }
  }

  public endTemplateLoading(): void {
    this.isTemplateApplied = true;

    this.removeInProgressChanges(TEMPLATE_PROCESSING_KEY);
  }

  public getPosterTypeObjectForResize(): PosterTypeObject {
    let posterType = this.type.toObject();
    posterType = {
      ...posterType,
      width: this.width,
      height: this.height,
    };

    return posterType;
  }

  public async replacePosterWithBackendPosterObject(backendPosterObjectToReplaceWith: PosterObjectBackend, maintainDimensions = true): Promise<void> {
    const posterLoadObject = getPosterObjectFromPosterBackendObject(backendPosterObjectToReplaceWith);
    return window.posterEditor?.whiteboard?.replacePoster(posterLoadObject, {maintainDimensions});
  }

  public async replacePoster(
    posterObjectToReplaceWith: PosterLoadObject,
    {maintainDimensions = true, isForGalleryTemplate = false, undoable = true, showResizeSuccessMessage = true}: ReplacePosterOpts = {}
  ): Promise<void> {
    this.startTemplateLoading();

    const posterTypeForResize = this.getPosterTypeObjectForResize();
    const isCurrentPosterCustomSize = this.type.isCustomSize();

    const numberOfPagesInCurrentPoster = this.pages.getNumberOfPages();
    const numberOfPagesInPosterToReplaceWith = Object.keys(posterObjectToReplaceWith.pages.pagesHashMap).length;

    if (numberOfPagesInPosterToReplaceWith === 1) {
      const pageIdForPosterBeingAdded = Object.keys(posterObjectToReplaceWith.pages.pagesHashMap)[0];
      await this.pages.pagesHashMap[this.viewportPageId].replacePageWithObject(posterObjectToReplaceWith.pages.pagesHashMap[pageIdForPosterBeingAdded]);
    }

    if (numberOfPagesInCurrentPoster === 1) {
      if (isForGalleryTemplate) {
        await this.updateFromObject(
          {
            isTemplate: false,
            idTemplate: posterObjectToReplaceWith.id,
            idGalleryTemplate: posterObjectToReplaceWith.id,
            idGalleryTemplateCreator: posterObjectToReplaceWith.idGalleryTemplateCreator,
          },
          {undoable: false}
        );
      }

      await this.updateFromObject(
        {
          type: posterObjectToReplaceWith.type,
          width: posterObjectToReplaceWith.width,
          height: posterObjectToReplaceWith.height,
          userWidth: posterObjectToReplaceWith.userWidth,
          userHeight: posterObjectToReplaceWith.userHeight,
          units: posterObjectToReplaceWith.units,
          audioClips: {
            ...posterObjectToReplaceWith.audioClips,
          },
        },
        {undoable: false}
      );
    }

    if (maintainDimensions) {
      resizePosterForResizeData(
        {
          isCustomDimension: isCurrentPosterCustomSize,
          posterType: posterTypeForResize,
        },
        undoable,
        showResizeSuccessMessage
      );
    }

    this.endTemplateLoading();
  }
}
